import LinedInput, {InputType} from "./LinedInput";
import React from "react";

const strings = {
    max: "Value must be less than or equal to",
};

type Props = React.ComponentProps<"input"> & {
    setValue: (s: string) => void;
    id: string;
    value: string;
    label: string;
    extraInfo?: string;
    validationMessage?: string[];
    showValidIcon?: boolean;
    allowDecimals?: boolean;
};

function MoneyInput(
    {setValue, max, value, allowDecimals = false, validationMessage, ...rest}: Props,
    ref: React.ForwardedRef<HTMLInputElement>,
) {
    function handleChange(e: React.ChangeEvent<HTMLInputElement>) {
        let newValue = e.target.value;
        let hasDecimal = false;
        let endsWithDecimal = false;
        let endsWithZeroInTenths = false;
        let endsWithZeroInHundredths = false;
        let endsWithZeroInTenthsAndHundredths = false;

        // remove non-number characters except for 1 decimal
        for (let i = 0; i < newValue.length; i++) {
            while (newValue.length > i && isNaN(parseInt(newValue[i]))) {
                if (allowDecimals && i !== 0 && !hasDecimal && newValue[i] === ".") {
                    hasDecimal = true;
                    break;
                }

                newValue = newValue.slice(0, i).concat(newValue.slice(i + 1));
            }
        }

        // determine if need to preserve user-entered decimal or 0s
        endsWithDecimal = hasDecimal && newValue[newValue.length - 1] === ".";
        if (hasDecimal && !endsWithDecimal) {
            const decimalIndex = newValue.indexOf(".");
            if (newValue[decimalIndex + 1] === "0" && newValue[decimalIndex + 2] === undefined) {
                endsWithZeroInTenths = true;
            } else if (newValue[decimalIndex + 1] !== "0" && newValue[decimalIndex + 2] === "0") {
                endsWithZeroInHundredths = true;
            } else if (newValue[decimalIndex + 1] === "0" && newValue[decimalIndex + 2] === "0") {
                endsWithZeroInTenthsAndHundredths = true;
            }
        }

        // add dollar sign and commas to newValue and preserve any user-entered decimal or 0s
        newValue =
            newValue === ""
                ? ""
                : "$" + parseFloat(newValue).toLocaleString("en-US", {maximumFractionDigits: 2});
        if (endsWithDecimal) {
            newValue = newValue + ".";
        } else if (endsWithZeroInTenths) {
            newValue = newValue + ".0";
        } else if (endsWithZeroInHundredths) {
            newValue = newValue + "0";
        } else if (endsWithZeroInTenthsAndHundredths) {
            newValue = newValue + ".00";
        }

        setValue(newValue);
    }

    const currentNumValue = parseFloat(value.substring(1).replace(/,/g, ""));
    let pattern = undefined;
    if (max !== undefined && currentNumValue > max) {
        pattern = `^\\$${max.toLocaleString()}$`;
    }

    let finalValidation: string[] = [];
    if (pattern && max) {
        finalValidation.push(`${strings.max} $${max.toLocaleString()}.`);
    }
    if (validationMessage) {
        finalValidation = finalValidation.concat(validationMessage);
    }

    return (
        <LinedInput
            {...rest}
            value={value}
            onChange={handleChange}
            type={InputType.Text}
            ref={ref}
            pattern={pattern}
            validationMessage={finalValidation}
        />
    );
}

export default React.forwardRef<HTMLInputElement, Props>(MoneyInput);
